import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Puck } from '@measured/puck';
import '@measured/puck/puck.css';
import { ModernButton } from '../ui/ModernButton';
import { ArrowLeftIcon } from '../ui/Icons';
import { puckConfig } from '../puck/puckConfig';
import { ImageGalleryPage } from './ImageGalleryPage';
import { dbService } from '../../services/DatabaseService';
import campaignService from '../../services/CampaignService';

export function StandalonePuckEditor({ campaign, onBack, onSave, onManualSave, companyName, campaignName, landingPageName, onEditRoute, onImageUpload }) {
  const remapWholeTemplate = (data) => data || { content: [], root: { props: {} } };

  const [puckData, setPuckData] = useState(() => {
    // Start with empty data - will be loaded from API
    return {
      content: [],
      root: { props: {} }
    };
  });
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isTplLoading, setIsTplLoading] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [puckKey, setPuckKey] = useState(0);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saving', 'saved', 'error'
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [notification, setNotification] = useState(null);
  const autosaveTimeoutRef = useRef(null);
  const unsavedChangesTimeoutRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Theme state for standalone editor
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'light' || saved === 'dark') return saved;
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch {}
    return 'light';
  });

  // Sync theme with localStorage and apply to document
  useEffect(() => {
    localStorage.setItem('theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [theme]);

  // Set document title
  useEffect(() => {
    // Check if we're on karrieresichern.de domain
    if (window.location.hostname === 'karrieresichern.de') {
      document.title = 'Karrieresichern';
    } else if (companyName && campaignName) {
      document.title = `Landing Page Editor - ${companyName} - ${campaignName}`;
    } else {
      document.title = 'Landing Page Editor';
    }
  }, [companyName, campaignName, landingPageName]);

  // Store campaign ID for dynamic URL replacement
  const [campaignId, setCampaignId] = useState(null);

  // Load campaign data and templates
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsTplLoading(true);
        
        // Fetch templates
        const list = await dbService.getLandingPageTemplates();
        setTemplates(list);
        
        // Fetch campaign data if we have the route parameters
        if (companyName && campaignName) {
          try {
            const campaignData = await campaignService.getCampaignByRoute(companyName, campaignName, landingPageName);
            if (campaignData.success) {
              const campaign = campaignData.data;
              setCampaignId(campaign.id); // Store campaign ID for dynamic replacement
              
              const initialData = campaign?.landing_page_data || {
                content: [],
                root: { props: {} }
              };
              
              // Transform all CTA URLs to use actual campaign ID
              const transformUrls = (data) => {
                if (!data || !campaign?.id) return data;
                
                const transformComponent = (component) => {
                  if (!component) return component;
                  
                  // Transform CTA Quiz Button URLs
                  if (component.type === 'CTA Quiz Button') {
                    return {
                      ...component,
                      props: {
                        ...component.props,
                        url: component.props.url?.replace('campaign-id', campaign.id) || `/apply/${campaign.id}`
                      }
                    };
                  }
                  
                  // Transform Hero Section button URLs
                  if (component.type === 'Hero-Bereich' && component.props?.buttons) {
                    return {
                      ...component,
                      props: {
                        ...component.props,
                        buttons: component.props.buttons.map(button => ({
                          ...button,
                          href: button.href?.replace('campaign-id', campaign.id) || `/apply/${campaign.id}`
                        }))
                      }
                    };
                  }
                  
                  // Recursively transform nested content
                  if (component.props?.content && Array.isArray(component.props.content)) {
                    return {
                      ...component,
                      props: {
                        ...component.props,
                        content: component.props.content.map(transformComponent)
                      }
                    };
                  }
                  
                  return component;
                };
                
                return {
                  ...data,
                  content: data.content?.map(transformComponent) || [],
                  zones: data.zones ? Object.keys(data.zones).reduce((acc, key) => {
                    acc[key] = data.zones[key].map(transformComponent);
                    return acc;
                  }, {}) : {}
                };
              };
              
              const transformedData = transformUrls(initialData);
              setPuckData(transformedData);
              setPuckKey(prev => prev + 1); // Force re-render
              console.log('Loaded existing landing page data:', transformedData);
            } else {
              console.log('No existing landing page data found, starting with empty page');
            }
          } catch (error) {
            console.error('Failed to load campaign data:', error);
          }
        }
      } catch (e) {
        console.error('Failed to load landing page templates:', e);
      } finally {
        setIsTplLoading(false);
      }
    };
    fetchData();
  }, [companyName, campaignName, landingPageName]);

  // Autosave function with debouncing
  const autosave = useCallback(async (data) => {
    if (!onSave || isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    setSaveStatus('saving');
    
    try {
      const normalized = {
        ...data,
        content: data.content,
        zones: data.zones
      };
      await onSave(normalized);
      setSaveStatus('saved');
      // Clear unsaved changes after successful save
      setHasUnsavedChanges(false);
      // Clear the unsaved changes timeout since we've saved successfully
      if (unsavedChangesTimeoutRef.current) {
        clearTimeout(unsavedChangesTimeoutRef.current);
        unsavedChangesTimeoutRef.current = null;
      }
    } catch (error) {
      console.error('Autosave failed:', error);
      setSaveStatus('error');
      // Keep hasUnsavedChanges as true if autosave fails
    }
  }, [onSave]);

  // Debounced autosave
  const debouncedAutosave = useCallback((data) => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    autosaveTimeoutRef.current = setTimeout(() => {
      autosave(data);
    }, 2000); // 2 second delay
  }, [autosave]);

  // Transform URLs in data to use actual campaign ID
  const transformUrlsInData = useCallback((data) => {
    if (!data || !campaignId) return data;
    
    const transformComponent = (component) => {
      if (!component) return component;
      
      // Transform CTA Quiz Button URLs
      if (component.type === 'CTA Quiz Button') {
        return {
          ...component,
          props: {
            ...component.props,
            url: component.props.url?.replace('campaign-id', campaignId) || `/apply/${campaignId}`
          }
        };
      }
      
      // Transform Hero Section button URLs
      if (component.type === 'Hero-Bereich' && component.props?.buttons) {
        return {
          ...component,
          props: {
            ...component.props,
            buttons: component.props.buttons.map(button => ({
              ...button,
              href: button.href?.replace('campaign-id', campaignId) || `/apply/${campaignId}`
            }))
          }
        };
      }
      
      // Recursively transform nested content
      if (component.props?.content && Array.isArray(component.props.content)) {
        return {
          ...component,
          props: {
            ...component.props,
            content: component.props.content.map(transformComponent)
          }
        };
      }
      
      return component;
    };
    
    return {
      ...data,
      content: data.content?.map(transformComponent) || [],
      zones: data.zones ? Object.keys(data.zones).reduce((acc, key) => {
        acc[key] = data.zones[key].map(transformComponent);
        return acc;
      }, {}) : {}
    };
  }, [campaignId]);

  // Handle data changes from Puck
  const handleDataChange = useCallback((data) => {
    // Transform URLs before setting data
    const transformedData = transformUrlsInData(data);
    setPuckData(transformedData);
    
    // Clear any existing unsaved changes timeout
    if (unsavedChangesTimeoutRef.current) {
      clearTimeout(unsavedChangesTimeoutRef.current);
    }
    
    // Set unsaved changes after a delay (longer than autosave delay)
    // This way, if autosave completes successfully, it will clear the flag before this fires
    unsavedChangesTimeoutRef.current = setTimeout(() => {
      setHasUnsavedChanges(true);
    }, 3000); // 3 second delay (longer than 2 second autosave)
    debouncedAutosave(transformedData);
  }, [debouncedAutosave, transformUrlsInData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
      if (unsavedChangesTimeoutRef.current) {
        clearTimeout(unsavedChangesTimeoutRef.current);
      }
    };
  }, []);

  // Handle manual save
  const handleSave = useCallback(async () => {
    if (!onManualSave) return;
    
    setSaveStatus('saving');
    try {
      const normalized = {
        ...puckData,
        content: puckData.content,
        zones: puckData.zones
      };
      await onManualSave(normalized);
      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      
      // Show success notification
      setNotification({
        type: 'success',
        message: 'Landing Page erfolgreich gespeichert!'
      });
      
      // Auto-hide notification after 3 seconds
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } catch (error) {
      console.error('Save failed:', error);
      setSaveStatus('error');
      
      // Show error notification
      setNotification({
        type: 'error',
        message: 'Fehler beim Speichern. Bitte versuchen Sie es erneut.'
      });
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setNotification(null);
      }, 5000);
    }
  }, [onManualSave, puckData]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    // Always go back without warning since autosave is enabled
    onBack();
  }, [onBack]);

  // Handle image upload
  const handleImageUpload = useCallback(() => {
    setShowImageGallery(true);
  }, []);

  // Handle back from gallery
  const handleBackFromGallery = useCallback(() => {
    setShowImageGallery(false);
  }, []);

  // Delete template function
  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!templateId || !templateName) return;
    
    setTemplateToDelete({ id: templateId, name: templateName });
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    
    try {
      await dbService.deleteLandingPageTemplate(templateToDelete.id);
      setTemplates(prev => prev.filter(t => t.id !== templateToDelete.id));
      setShowDeleteConfirmModal(false);
      setTemplateToDelete(null);
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Fehler beim Löschen der Vorlage: ' + error.message);
    }
  };

  // Update CTA Quiz Button URLs when campaign ID changes
  useEffect(() => {
    if (campaign?.id) {
      setPuckData(prevData => {
        const updatedContent = prevData.content.map(component => {
          if (component.type === 'CTA Quiz Button') {
            return {
              ...component,
              props: {
                ...component.props,
                url: `/apply/${campaign.id}`
              }
            };
          }
          return component;
        });
        
        return {
          ...prevData,
          content: updatedContent
        };
      });
    }
  }, [campaign?.id]);

  // Custom header with preview button next to publish
  const customHeader = () => (
    <div className="flex items-center justify-between w-full py-4">
      <div className="flex items-center space-x-4 min-w-0 flex-1">
        <ModernButton
          onClick={handleBack}
          variant="secondary"
          size="sm"
          icon={<ArrowLeftIcon className="w-4 h-4" />}
        >
          Zurück
        </ModernButton>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">
            Landing Page bearbeiten
          </h1>
          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
              {campaign?.name || `${companyName}/${campaignName}`}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              {getUnsavedChangesIcon()}
              {getSaveStatusIcon()}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-3 shrink-0">
        {/* Templates dropdown with delete functionality */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              className="px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-200"
              disabled={isTplLoading}
              onChange={(e) => {
                const tpl = templates.find(t => t.id === e.target.value);
                if (!tpl) return;
                try {
                  const tplData = typeof tpl.landing_page_json === 'string' ? JSON.parse(tpl.landing_page_json) : tpl.landing_page_json;
                  const remapped = remapWholeTemplate(tplData || { content: [], root: { props: {} }, zones: {} });
                  
                  // Fix CTA Quiz Button URLs in templates
                  const processedData = {
                    ...remapped,
                    content: (remapped.content || []).map(component => {
                      if (component.type === 'CTA Quiz Button' && campaign?.id) {
                        return {
                          ...component,
                          props: {
                            ...component.props,
                            url: `/apply/${campaign.id}`
                          }
                        };
                      }
                      return component;
                    })
                  };
                  
                  setPuckData(processedData);
                  setPuckKey(prev => prev + 1);
                  // trigger a small tick so Puck re-renders its internal editor state
                  setTimeout(() => {
                    debouncedAutosave(processedData);
                  }, 0);
                } catch (err) {
                  console.error('Invalid template JSON:', err);
                }
              }}
              defaultValue=""
              title="Vorlage laden"
            >
              <option value="" disabled>{isTplLoading ? 'Vorlagen laden...' : 'Vorlage wählen'}</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          {templates.length > 0 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                const flexContainer = e.target.closest('.flex');
                const select = flexContainer?.querySelector('select');
                const selectedTemplateId = select?.value;
                if (selectedTemplateId) {
                  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
                  if (selectedTemplate) {
                    handleDeleteTemplate(selectedTemplate.id, selectedTemplate.name);
                  }
                }
              }}
              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Vorlage löschen"
              disabled={isTplLoading}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
        <ModernButton
          onClick={() => setShowSaveTemplateModal(true)}
          variant="secondary"
          size="sm"
          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
        >
          Als Vorlage speichern
        </ModernButton>
        <ModernButton
          onClick={handleImageUpload}
          variant="primary"
          size="sm"
          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
        >
          Bilder
        </ModernButton>
        {onEditRoute && campaign?.customRoute && (
          <ModernButton
            onClick={onEditRoute}
            variant="secondary"
            size="sm"
            icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>}
          >
            Route bearbeiten
          </ModernButton>
        )}
        {companyName && campaignName && (
          <ModernButton
            onClick={() => {
              const landingPage = landingPageName || 'landing';
              window.open(`/${companyName}/${campaignName}/${landingPage}`, '_blank');
            }}
            variant="secondary"
            size="sm"
            icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>}
          >
            Vorschau
          </ModernButton>
        )}
        <ModernButton
          onClick={handleSave}
          variant="primary"
          size="sm"
          disabled={saveStatus === 'saving'}
          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" /></svg>}
        >
          {saveStatus === 'saving' ? 'Speichern...' : 'Speichern'}
        </ModernButton>
      </div>
    </div>
  );

  // Helper functions for status icons
  const getUnsavedChangesIcon = () => {
    if (!hasUnsavedChanges) return null;
    return (
      <div className="w-2 h-2 bg-amber-500 rounded-full" title="Ungespeicherte Änderungen" />
    );
  };

  const getSaveStatusIcon = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" title="Speichern..." />
        );
      case 'saved':
        return (
          <div className="w-4 h-4 text-green-500" title="Gespeichert">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="w-4 h-4 text-red-500" title="Fehler beim Speichern">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  // Show image gallery page if requested
  if (showImageGallery) {
    return <ImageGalleryPage onBack={handleBackFromGallery} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Theme Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          className="p-3 bg-white/90 dark:bg-slate-800/90 backdrop-blur-xs border border-slate-200 dark:border-slate-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <span className="text-slate-700 dark:text-slate-300">
            {theme === 'dark' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </span>
        </button>
      </div>

      {/* Custom Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {customHeader()}
        </div>
      </div>

      {/* Puck Editor - Full Screen */}
      <div className="h-[calc(100vh-80px)]">
        <Puck 
          key={puckKey}
          config={puckConfig} 
          data={puckData} 
          onPublish={handleSave}
          onChange={handleDataChange}
        />
      </div>

      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Vorlage speichern</h3>
                <button onClick={() => setShowSaveTemplateModal(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Name der Vorlage *</label>
                  <input
                    type="text"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    placeholder="z.B. Standard-CTA-Seite"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowSaveTemplateModal(false)} className="px-4 py-2 bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-100 rounded-lg">Abbrechen</button>
                  <button
                    onClick={async () => {
                      if (!newTemplateName.trim()) return;
                      try {
                        const tpl = await dbService.createLandingPageTemplate(newTemplateName.trim(), JSON.stringify(puckData));
                        setTemplates(prev => [tpl, ...prev]);
                        setShowSaveTemplateModal(false);
                        setNewTemplateName('');
                      } catch (e) {
                        console.error('Save template failed:', e);
                      }
                    }}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                  >
                    Speichern
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Template Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Vorlage löschen</h3>
                <button 
                  onClick={() => setShowDeleteConfirmModal(false)} 
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <p className="text-slate-700 dark:text-slate-300">
                  Möchten Sie die Vorlage <strong>"{templateToDelete?.name}"</strong> wirklich löschen?
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
                <div className="flex justify-end gap-2">
                  <button 
                    onClick={() => setShowDeleteConfirmModal(false)} 
                    className="px-4 py-2 bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-100 rounded-lg"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={confirmDeleteTemplate}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                  >
                    Löschen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-pulse">
          <div className={`px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 border-2 backdrop-blur-xs ${
            notification.type === 'success' 
              ? 'bg-green-600 text-white border-green-700' 
              : 'bg-red-600 text-white border-red-700'
          }`}>
            {notification.type === 'success' ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
            <span className="font-bold text-lg">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 text-white hover:text-gray-200 bg-black bg-opacity-20 rounded-full p-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
